export interface PerformanceMetrics {
  memory: {
    used: number;
    limit: number;
    timestamp: number;
  };
  videos: {
    poolSize: number;
    inUse: number;
    cacheHitRate: number;
    totalLoaded: number;
  };
  loadTimes: {
    average: number;
    last: number;
    samples: number[];
  };
  network: {
    bytesLoaded: number;
    activeRequests: number;
  };
}

class PerformanceTracker {
  private metrics: PerformanceMetrics = {
    memory: {
      used: 0,
      limit: 0,
      timestamp: Date.now(),
    },
    videos: {
      poolSize: 0,
      inUse: 0,
      cacheHitRate: 0,
      totalLoaded: 0,
    },
    loadTimes: {
      average: 0,
      last: 0,
      samples: [],
    },
    network: {
      bytesLoaded: 0,
      activeRequests: 0,
    },
  };

  private loadTimeStart: Map<string, number> = new Map();
  private maxSamples = 50;

  // Update memory metrics
  updateMemory(): void {
    if ('memory' in performance && performance.memory) {
      const memInfo = performance.memory;
      this.metrics.memory = {
        used: memInfo.usedJSHeapSize,
        limit: memInfo.jsHeapSizeLimit,
        timestamp: Date.now(),
      };
    }
  }

  // Track video load start
  startVideoLoad(src: string): void {
    this.loadTimeStart.set(src, performance.now());
    this.metrics.network.activeRequests++;
  }

  // Track video load end
  endVideoLoad(src: string, success: boolean = true): void {
    const startTime = this.loadTimeStart.get(src);
    if (startTime) {
      const loadTime = performance.now() - startTime;
      this.metrics.loadTimes.samples.push(loadTime);

      // Keep only last N samples
      if (this.metrics.loadTimes.samples.length > this.maxSamples) {
        this.metrics.loadTimes.samples.shift();
      }

      this.metrics.loadTimes.last = loadTime;
      this.metrics.loadTimes.average =
        this.metrics.loadTimes.samples.reduce((a, b) => a + b, 0) /
        this.metrics.loadTimes.samples.length;

      this.loadTimeStart.delete(src);
    }

    if (this.metrics.network.activeRequests > 0) {
      this.metrics.network.activeRequests--;
    }

    if (success) {
      this.metrics.videos.totalLoaded++;
    }
  }

  // Update video pool metrics
  updateVideoMetrics(poolMetrics: { poolSize: number; inUse: number; cacheHitRate: number }): void {
    this.metrics.videos = {
      ...this.metrics.videos,
      ...poolMetrics,
    };
  }

  // Update network bytes
  addNetworkBytes(bytes: number): void {
    this.metrics.network.bytesLoaded += bytes;
  }

  // Get current metrics
  getMetrics(): PerformanceMetrics {
    this.updateMemory();
    return { ...this.metrics };
  }

  // Reset metrics
  reset(): void {
    this.metrics = {
      memory: {
        used: 0,
        limit: 0,
        timestamp: Date.now(),
      },
      videos: {
        poolSize: 0,
        inUse: 0,
        cacheHitRate: 0,
        totalLoaded: 0,
      },
      loadTimes: {
        average: 0,
        last: 0,
        samples: [],
      },
      network: {
        bytesLoaded: 0,
        activeRequests: 0,
      },
    };
    this.loadTimeStart.clear();
  }

  // Format bytes to human readable
  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Format time to human readable
  static formatTime(ms: number): string {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }
}

// HMR-safe singleton using Symbol for cleaner global storage
const TRACKER_KEY = Symbol.for('presentr.performanceTracker');

interface GlobalWithTracker {
  [TRACKER_KEY]?: PerformanceTracker;
}

// Store singleton in globalThis for HMR compatibility
export const getPerformanceTracker = (): PerformanceTracker => {
  const global = globalThis as GlobalWithTracker;
  if (!global[TRACKER_KEY]) {
    global[TRACKER_KEY] = new PerformanceTracker();
  }
  return global[TRACKER_KEY];
};

export const resetPerformanceTracker = (): void => {
  const global = globalThis as GlobalWithTracker;
  if (global[TRACKER_KEY]) {
    global[TRACKER_KEY].reset();
    global[TRACKER_KEY] = undefined;
  }
};

export default PerformanceTracker;
